import React, { useEffect, useState } from "react";
import { Landmark, Mail, ShieldCheck, Loader2, ArrowRight, MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { useSeo } from "@/hooks/useSeo";

// Two-step wizard: request → verify. Once verified the user is auto-added to
// db.gov_officials and lands on /gov/admin.
export default function OfficialsOnboarding() {
  const { user } = useAuth();
  const [me, setMe] = useState(null);
  const [step, setStep] = useState(1);
  const [req, setReq] = useState({ work_email: "", city: "", designation: "", department: "" });
  const [wards, setWards] = useState([]);          // multi-select chips
  const [wardDraft, setWardDraft] = useState("");
  const [otp, setOtp] = useState("");
  const [busy, setBusy] = useState(false);

  useSeo({ title: "Officials Onboarding · find2near",
           description: "Claim your city admin seat with a verified work email.", canonical: "/officials/onboard" });

  useEffect(() => {
    if (user) api.get("/gov/officials/me").then((r) => setMe(r.data)).catch(() => {});
  }, [user]);

  const addWard = () => {
    const w = wardDraft.trim();
    if (!w) return;
    // Dedupe case-insensitively; cap at 20.
    const exists = wards.some((x) => x.toLowerCase() === w.toLowerCase());
    if (!exists && wards.length < 20) setWards([...wards, w]);
    setWardDraft("");
  };
  const removeWard = (w) => setWards(wards.filter((x) => x !== w));

  const request = async () => {
    if (!req.work_email || !req.city) { toast.error("Work email + city required"); return; }
    setBusy(true);
    try {
      const r = await api.post("/gov/officials/request", { ...req, wards });
      // Preview environment surfaces the OTP so the demo works without inboxes.
      toast.success(`OTP sent to ${r.data.email} — preview OTP: ${r.data.otp_preview}`);
      setStep(2);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const verify = async () => {
    if (!otp.trim()) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      const r = await api.post("/gov/officials/verify", { otp });
      toast.success(`Email verified — your ${r.data.designation || "official"} application for ${r.data.city} is pending admin review`);
      api.get("/gov/officials/me").then((m) => setMe(m.data)).catch(() => {});
    } catch (e) { toast.error(e.response?.data?.detail || "Wrong OTP"); }
    finally { setBusy(false); }
  };

  if (!user) return <div className="p-12 text-center"><Link to="/onboarding" className="text-[#E05E36] font-bold">Sign in first</Link> to continue.</div>;
  if (!me?.is_official && me?.application_status === "pending_admin_review") {
    return (
      <div className="max-w-xl mx-auto p-12 text-center" data-testid="official-application-pending">
        <h1 className="text-2xl font-black text-stone-900">Application under review</h1>
        <p className="text-sm text-stone-600 mt-2">Your work email is verified. A Find2Near admin will review your official access before any official tools are enabled.</p>
      </div>
    );
  }
  if (me?.is_official) {
    const officialWards = me.official?.wards || [];
    return (
      <div className="max-w-md mx-auto text-center py-16 px-6" data-testid="officials-already">
        <ShieldCheck className="mx-auto text-emerald-600" size={44} />
        <h2 className="heading text-2xl font-black mt-3">You're already verified</h2>
        <div className="text-sm text-stone-600 mt-1">{me.official?.designation} · {me.official?.city}</div>
        {officialWards.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5 justify-center" data-testid="officials-wards">
            {officialWards.map((w) => (
              <span key={w} className="inline-flex items-center gap-1 text-[11px] font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full px-2.5 py-1">
                <MapPin size={10} /> {w}
              </span>
            ))}
          </div>
        )}
        <Link to="/gov/admin" className="inline-flex items-center gap-1 mt-6 bg-[#2D5A27] text-white font-black px-6 py-3 rounded-full">Open dashboard <ArrowRight size={14} /></Link>
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-10" data-testid="officials-onboarding">
      <div className="text-xs uppercase tracking-widest text-stone-500 font-bold flex items-center gap-2"><Landmark size={12} /> Officials onboarding</div>
      <h1 className="heading text-3xl font-black text-stone-900">Claim your seat.</h1>
      <p className="text-stone-500 text-sm mt-1">Verified officials unlock the analytics dashboard + emergency broadcast.</p>

      {step === 1 && (
        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-5 space-y-2">
          <input value={req.work_email} onChange={(e) => setReq({ ...req, work_email: e.target.value })} placeholder="Work email (e.g. admin@mumbai.gov.in)" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="off-email" />
          <input value={req.city} onChange={(e) => setReq({ ...req, city: e.target.value })} placeholder="City" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="off-city" />
          <input value={req.designation} onChange={(e) => setReq({ ...req, designation: e.target.value })} placeholder="Designation (e.g. Ward Officer)" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="off-designation" />
          <input value={req.department} onChange={(e) => setReq({ ...req, department: e.target.value })} placeholder="Department (optional)" className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none" data-testid="off-dept" />

          {/* Ward multi-select — type a ward name, press Enter/comma to add.
              Chips can be removed; empty list means the official sees all
              grievances in the city by default. */}
          <div className="pt-2">
            <div className="text-[11px] uppercase tracking-widest text-stone-500 font-bold flex items-center gap-1 mb-1"><MapPin size={11} /> Wards you cover ({wards.length}/20)</div>
            <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="ward-chips">
              {wards.map((w) => (
                <span key={w} className="inline-flex items-center gap-1 text-xs font-bold bg-emerald-50 text-emerald-800 border border-emerald-200 rounded-full pl-3 pr-1.5 py-1" data-testid={`ward-chip-${w}`}>
                  {w}
                  <button onClick={() => removeWard(w)} className="hover:bg-emerald-100 rounded-full p-0.5" aria-label={`Remove ${w}`}><X size={11} /></button>
                </span>
              ))}
            </div>
            <input
              value={wardDraft}
              onChange={(e) => setWardDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === ",") { e.preventDefault(); addWard(); }
              }}
              onBlur={addWard}
              placeholder="Add a ward and press Enter (e.g. Ward 12, Andheri West)"
              className="w-full px-4 py-2 rounded-full border border-stone-200 text-sm outline-none"
              data-testid="off-ward-input"
            />
            <div className="text-[10px] text-stone-400 mt-1">Skip this if you're a city-wide officer — you can add wards later.</div>
          </div>

          <button onClick={request} disabled={busy} className="mt-2 w-full bg-[#2D5A27] hover:bg-emerald-800 text-white font-black py-2.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="off-request">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Mail size={14} />} Send verification OTP
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="mt-6 rounded-3xl bg-white border border-stone-200 p-5">
          <div className="text-sm text-stone-700 mb-2">Enter the 6-digit code sent to <b>{req.work_email}</b>.</div>
          <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="000000" className="w-full px-4 py-3 rounded-full border border-stone-200 text-center text-xl font-black tracking-widest outline-none" data-testid="off-otp" />
          <button onClick={verify} disabled={busy} className="mt-3 w-full bg-[#E05E36] hover:bg-[#c94e2a] text-white font-black py-2.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="off-verify">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />} Verify & unlock
          </button>
          <button onClick={() => setStep(1)} className="mt-2 w-full text-xs text-stone-500 hover:text-stone-800" data-testid="off-back">← Resend or edit details</button>
        </div>
      )}
    </div>
  );
}
